// ATTENTION : L'article 6 III de la LCEN impose l'affichage des informations d'hébergeur.
// La section "Hébergeur du site" doit être ajoutée dès que l'hébergement définitif sera choisi.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const SectionTitle = ({ children, testId }) => (
  <h2
    data-testid={testId}
    className="font-serif text-2xl md:text-3xl tracking-tight text-[#0A0A0A] mt-16 mb-6"
  >
    {children}
    <span
      className="block w-10 h-[2px] bg-[#E10600] mt-4"
      aria-hidden="true"
    />
  </h2>
);

const MentionsLegales = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="bg-[#FAFAFA] min-h-screen flex flex-col">
      <Header forceSolid />
      <main className="flex-1 pt-32 md:pt-40 pb-24 md:pb-32" data-testid="legal-main">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-[720px] px-6 md:px-8"
        >
          {/* Back link */}
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#6B7280] hover:text-[#E10600] font-medium transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            <span>Retour à l'accueil</span>
          </Link>

          {/* Main title */}
          <h1
            data-testid="legal-title"
            className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] mt-10 mb-6"
          >
            Mentions <span className="italic font-normal">légales.</span>
          </h1>

          <span className="block w-12 h-[2px] bg-[#E10600] mb-12" aria-hidden="true" />

          <div className="legal-body text-[#374151] font-light leading-[1.75] text-[16px] md:text-[17px] space-y-5">
            {/* 1. Éditeur du site */}
            <SectionTitle testId="legal-section-editeur">1. Éditeur du site</SectionTitle>
            <p>
              Le présent site est édité par{" "}
              <strong className="text-[#0A0A0A] font-medium">
                Monsieur Jacques Marcel Georges LENGELLE
              </strong>
              , <strong className="text-[#0A0A0A] font-medium">entrepreneur individuel</strong>{" "}
              exerçant sous la dénomination commerciale{" "}
              <strong className="text-[#0A0A0A] font-medium">LJMG</strong>, dont l'établissement
              principal est situé{" "}
              <strong className="text-[#0A0A0A] font-medium">
                953 Boulevard d'Alger, 83600 Fréjus, France
              </strong>
              .
            </p>
            <p>
              SIREN&nbsp;: <strong className="text-[#0A0A0A] font-medium">414 271 437</strong>
              <br />
              SIRET&nbsp;:{" "}
              <strong className="text-[#0A0A0A] font-medium">414 271 437 00041</strong>
              <br />
              Code APE / NAF&nbsp;:{" "}
              <strong className="text-[#0A0A0A] font-medium">6831Z</strong>
              <br />
              TVA&nbsp;:{" "}
              <strong className="text-[#0A0A0A] font-medium">
                TVA non applicable, article 293 B du Code général des impôts
              </strong>{" "}
              (franchise en base de TVA)
              <br />
              Adresse électronique&nbsp;:{" "}
              <a
                href="mailto:lengellejacques@outlook.fr"
                className="text-[#0A0A0A] font-medium underline decoration-[#E10600] decoration-1 underline-offset-4 hover:text-[#E10600] transition-colors"
              >
                lengellejacques@outlook.fr
              </a>
            </p>

            {/* 2. Directeur de la publication */}
            <SectionTitle testId="legal-section-directeur">
              2. Directeur de la publication
            </SectionTitle>
            <p>
              Monsieur{" "}
              <strong className="text-[#0A0A0A] font-medium">
                Jacques Marcel Georges LENGELLE
              </strong>
              , en qualité d'entrepreneur individuel exploitant LJMG.
            </p>

            {/* 3. Propriété intellectuelle (anciennement 4) */}
            <SectionTitle testId="legal-section-propriete">
              3. Propriété intellectuelle
            </SectionTitle>
            <p>
              L'ensemble des contenus présents sur le site{" "}
              <strong className="text-[#0A0A0A] font-medium">ljmg-france.com</strong> (textes,
              illustrations, logos, photographies, vidéos, signes distinctifs, charte graphique,
              structure et arborescence) est la propriété exclusive de LJMG ou de ses partenaires,
              et est protégé par les lois françaises et internationales relatives à la propriété
              intellectuelle.
            </p>
            <p>
              Toute reproduction, représentation, modification, publication, transmission,
              dénaturation, totale ou partielle, du site ou de son contenu, par quelque procédé que
              ce soit, et sur quelque support que ce soit, est interdite sans l'autorisation écrite
              préalable de LJMG. Toute exploitation non autorisée est susceptible d'engager la
              responsabilité civile et pénale de son auteur.
            </p>

            {/* 4. Données personnelles (anciennement 5) */}
            <SectionTitle testId="legal-section-donnees">4. Données personnelles</SectionTitle>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
              « Informatique et Libertés » du 6 janvier 1978 modifiée, vous disposez d'un droit
              d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité
              des données personnelles vous concernant.
            </p>
            <p>
              Ces droits peuvent être exercés en adressant une demande écrite à&nbsp;:{" "}
              <a
                href="mailto:lengellejacques@outlook.fr"
                className="text-[#0A0A0A] font-medium underline decoration-[#E10600] decoration-1 underline-offset-4 hover:text-[#E10600] transition-colors"
              >
                lengellejacques@outlook.fr
              </a>
              .
            </p>
            <p>
              Le site <strong className="text-[#0A0A0A] font-medium">ljmg-france.com</strong> ne
              collecte aucune donnée personnelle à l'insu des visiteurs et n'utilise pas de cookies
              de traçage à des fins publicitaires.
            </p>

            {/* 5. Cookies (anciennement 6) */}
            <SectionTitle testId="legal-section-cookies">5. Cookies</SectionTitle>
            <p>
              Le site LJMG n'utilise pas de cookies de mesure d'audience ni de cookies tiers à des
              fins publicitaires. Seuls des cookies strictement nécessaires au bon fonctionnement du
              site peuvent être déposés.
            </p>

            {/* 6. Responsabilité (anciennement 7) */}
            <SectionTitle testId="legal-section-responsabilite">6. Responsabilité</SectionTitle>
            <p>
              LJMG s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur
              le site, dont elle se réserve le droit de modifier, à tout moment et sans préavis, le
              contenu. Toutefois, LJMG ne peut garantir l'exactitude, la précision ou l'exhaustivité
              des informations mises à disposition sur le site.
            </p>
            <p>
              En conséquence, LJMG décline toute responsabilité pour tout dommage résultant
              notamment d'une imprécision ou inexactitude des informations disponibles sur le site,
              ou pour toute atteinte résultant d'une intrusion frauduleuse d'un tiers entraînant une
              modification des informations mises à disposition.
            </p>

            {/* 7. Liens hypertextes (anciennement 8) */}
            <SectionTitle testId="legal-section-liens">7. Liens hypertextes</SectionTitle>
            <p>
              Le site LJMG peut contenir des liens hypertextes vers d'autres sites internet. LJMG
              n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur
              contenu.
            </p>

            {/* 8. Droit applicable (anciennement 9) */}
            <SectionTitle testId="legal-section-droit">8. Droit applicable</SectionTitle>
            <p>
              Les présentes mentions légales sont régies par le droit français. En cas de litige, et
              à défaut d'accord amiable, les tribunaux français seront seuls compétents.
            </p>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default MentionsLegales;
